import { create } from "zustand";
import type { ManifestLayer } from "../types/contract";
import {
  deleteRecSet,
  hydrateFertRx,
  loadRecSets,
  type SavedRecSet,
} from "../lib/fertSets";

// Recs are computed from an explicit config (source maps + nutrients + optional BCSR
// cation balance) and saved as named sets (localStorage).
export interface FertSummary {
  avgLime: number;
  avgP: number;
  avgK: number;
  pctNeeding: number;
  mapped: number; // cells with at least one rec (the pctNeeding denominator)
  cells: number; // total sampled cells
}
interface FertEntry {
  status: "computing" | "ready" | "error";
  layers: ManifestLayer[]; // the active set's rec layers (on the map)
  summary: FertSummary | null;
  error: string | null;
  activeSetId: string | null;
  savedSets: SavedRecSet[];
  /** Set when the open Rx was saved on a boundary that is no longer current. */
  notice: string | null;
}
interface FertRecsState {
  byField: Record<string, FertEntry | undefined>;
  computing: (fieldId: string) => void;
  ready: (
    fieldId: string,
    layers: ManifestLayer[],
    summary: FertSummary,
    activeSetId: string | null,
    savedSets: SavedRecSet[],
    notice?: string | null,
  ) => void;
  fail: (fieldId: string, error: string) => void;
  refreshSaved: (fieldId: string) => void;
  removeSet: (fieldId: string, id: string) => void;
  clear: (fieldId: string) => void;
}

const entryOf = (s: FertRecsState, fieldId: string): FertEntry =>
  s.byField[fieldId] ?? {
    status: "ready",
    layers: [],
    summary: null,
    error: null,
    activeSetId: null,
    savedSets: loadRecSets(fieldId),
    notice: null,
  };

export const useFertRecs = create<FertRecsState>((set) => ({
  byField: {},
  computing: (fieldId) =>
    set((s) => ({
      byField: {
        ...s.byField,
        [fieldId]: {
          ...entryOf(s, fieldId),
          status: "computing",
          error: null,
          notice: null,
        },
      },
    })),
  ready: (fieldId, layers, summary, activeSetId, savedSets, notice = null) =>
    set((s) => ({
      byField: {
        ...s.byField,
        [fieldId]: {
          status: "ready",
          layers,
          summary,
          error: null,
          activeSetId,
          savedSets,
          notice,
        },
      },
    })),
  fail: (fieldId, error) =>
    set((s) => ({
      byField: {
        ...s.byField,
        [fieldId]: { ...entryOf(s, fieldId), status: "error", error },
      },
    })),
  refreshSaved: (fieldId) => {
    // immediate: localStorage cache; then reconcile from the server (server wins) and update
    set((s) => ({
      byField: {
        ...s.byField,
        [fieldId]: { ...entryOf(s, fieldId), savedSets: loadRecSets(fieldId) },
      },
    }));
    void hydrateFertRx(fieldId).then((savedSets) =>
      set((s) => ({
        byField: {
          ...s.byField,
          [fieldId]: { ...entryOf(s, fieldId), savedSets },
        },
      })),
    );
  },
  removeSet: (fieldId, id) =>
    set((s) => {
      const savedSets = deleteRecSet(fieldId, id);
      const cur = entryOf(s, fieldId);
      // if the deleted set was active, drop its layers from the map too
      const stillActive = cur.activeSetId && cur.activeSetId !== id;
      return {
        byField: {
          ...s.byField,
          [fieldId]: {
            ...cur,
            savedSets,
            layers: stillActive ? cur.layers : [],
            summary: stillActive ? cur.summary : null,
            activeSetId: stillActive ? cur.activeSetId : null,
          },
        },
      };
    }),
  clear: (fieldId) =>
    set((s) => {
      const next = { ...s.byField };
      delete next[fieldId];
      return { byField: next };
    }),
}));

export const NO_FERT_LAYERS: ManifestLayer[] = []; // stable empty ref for selectors
